/**
 * <BroadcastModal /> — segment-based announcement sender.
 *
 * Admins pick a segment (all / premium / country / role / …), preview the
 * recipient count, optionally send a test to themselves, then broadcast.
 * Supports {{first_name}} / {{name}} / {{email}} template variables.
 */
import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Send, X, Loader2, Users, TestTube2, Eye } from "lucide-react";

const SEGMENTS = [
  { kind: "all", label: "All users", needsValue: false, hint: "Everyone with an account." },
  { kind: "premium", label: "All premium subscribers", needsValue: false },
  { kind: "premium_tier", label: "Specific premium tier", needsValue: "tier_id", valueLabel: "Tier ID" },
  { kind: "editors_admins", label: "Editors + admins (internal team)", needsValue: false, hint: "Everyone with elevated role." },
  { kind: "country", label: "Users in a country", needsValue: "country", valueLabel: "Country name or ISO code (e.g. India / IN)" },
  { kind: "state", label: "Users in a state / region", needsValue: "state", valueLabel: "State name (e.g. Kerala)" },
  { kind: "role", label: "Users with a specific role", needsValue: "role", valueLabel: "Role (user / editor / admin / super_admin)" },
  { kind: "active_30d", label: "Users active in last 30 days", needsValue: false, hint: "Excludes dormant accounts." },
];

export default function BroadcastModal({ onClose }) {
  const [segment, setSegment] = useState(SEGMENTS[0]);
  const [value, setValue] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [wantEmail, setWantEmail] = useState(true);
  const [wantInbox, setWantInbox] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  // Debounced preview count
  useEffect(() => {
    if (segment.needsValue && !value.trim()) { setPreview(null); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await http.post("/admin/broadcast/preview", {
          subject: subject || "preview",
          message: message || "preview",
          segment: { kind: segment.kind, value: value.trim() || null },
          email: wantEmail, inbox: wantInbox,
        });
        setPreview(data);
      } catch { setPreview(null); }
    }, 300);
    return () => clearTimeout(t);
  }, [segment, value, subject, message, wantEmail, wantInbox]);

  const testSend = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Fill in subject and message first."); return; }
    setTesting(true);
    try {
      await http.post("/admin/broadcast", {
        subject: subject.trim(), message: message.trim(),
        segment: { kind: segment.kind, value: value.trim() || null },
        email: wantEmail, inbox: wantInbox, test_only: true,
      });
      toast.success("Test sent to you — check your inbox tile on /dashboard.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed.");
    } finally { setTesting(false); }
  };

  const send = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Fill in subject and message."); return; }
    const count = preview?.recipient_count || 0;
    if (count === 0) { toast.error("Nobody matches that segment."); return; }
    if (!window.confirm(`Send to ${count} recipient${count === 1 ? "" : "s"}? This can't be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await http.post("/admin/broadcast", {
        subject: subject.trim(), message: message.trim(),
        segment: { kind: segment.kind, value: value.trim() || null },
        email: wantEmail, inbox: wantInbox, test_only: false,
      });
      toast.success(`Broadcast sent — ${data.delivered_inbox} inbox, ${data.emails_queued} emails queued.`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Broadcast failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose} data-testid="broadcast-modal">
      <form onSubmit={(e) => { e.preventDefault(); send(); }} onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl p-6 space-y-4" style={{ background: "var(--wofy-bg)", border: "1px solid var(--wofy-border)" }}>
        <div className="flex items-center justify-between">
          <div>
            <div className="overline" style={{ color: "var(--wofy-primary)" }}>
              <Megaphone size={12} className="inline -mt-0.5 mr-1" /> BROADCAST
            </div>
            <h2 className="font-display text-2xl mt-1">Send a broadcast</h2>
            <p className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>
              Delivered to matching users' Dashboard inbox and (optionally) via email. Supports <code>{"{{first_name}}"}</code>, <code>{"{{name}}"}</code>, <code>{"{{email}}"}</code>.
            </p>
          </div>
          <button type="button" onClick={onClose} className="wofy-btn-ghost"><X className="w-4 h-4" /></button>
        </div>

        {/* Segment picker */}
        <div>
          <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Segment</span>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {SEGMENTS.map((s) => (
              <label key={s.kind} className={`cursor-pointer rounded-lg border p-2 text-sm ${segment.kind === s.kind ? "text-white" : ""}`} style={segment.kind === s.kind ? { background: "var(--wofy-primary)", borderColor: "transparent" } : { borderColor: "var(--wofy-border)" }} data-testid={`broadcast-segment-${s.kind}`}>
                <input type="radio" name="segment" checked={segment.kind === s.kind} onChange={() => { setSegment(s); setValue(""); }} className="sr-only" />
                <div className="font-semibold">{s.label}</div>
                {s.hint && <div className="text-[10px] opacity-70">{s.hint}</div>}
              </label>
            ))}
          </div>
          {segment.needsValue && (
            <input value={value} onChange={(e) => setValue(e.target.value)} placeholder={segment.valueLabel} className="wofy-input mt-2" data-testid="broadcast-segment-value" />
          )}
        </div>

        {/* Recipient count preview */}
        <div className="rounded-xl border p-3 text-sm flex items-center gap-3" style={{ background: "rgba(178,93,56,0.05)", borderColor: "var(--wofy-primary)" }} data-testid="broadcast-preview">
          <Users className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
          <div className="flex-1">
            {preview ? (
              <span>
                <b>{preview.recipient_count}</b> recipient{preview.recipient_count === 1 ? "" : "s"}
                {preview.recipient_count > 0 && (
                  <span className="opacity-70"> · {preview.with_email} with email · {preview.without_email} inbox-only</span>
                )}
              </span>
            ) : segment.needsValue && !value ? (
              <span className="opacity-60">Enter a value to preview count.</span>
            ) : (
              <span className="opacity-60">Calculating…</span>
            )}
          </div>
        </div>

        {/* Composition */}
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Subject *</span>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} className="wofy-input mt-1" required data-testid="broadcast-subject" />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Message body *</span>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={8} maxLength={10000} className="wofy-input mt-1" placeholder="Hi {{first_name}}, we just launched…" required data-testid="broadcast-body" />
          <div className="text-[11px] opacity-60 mt-1">
            Variables: <code>{"{{first_name}}"}</code>, <code>{"{{name}}"}</code>, <code>{"{{email}}"}</code>. Line breaks preserved.
          </div>
        </label>

        <div className="flex flex-wrap items-center gap-4 text-sm">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={wantInbox} onChange={(e) => setWantInbox(e.target.checked)} data-testid="broadcast-inbox" /> Dashboard inbox
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={wantEmail} onChange={(e) => setWantEmail(e.target.checked)} data-testid="broadcast-email" /> Email via Resend
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t" style={{ borderColor: "var(--wofy-line)" }}>
          <button type="button" onClick={onClose} className="wofy-btn-ghost">Cancel</button>
          <button type="button" onClick={testSend} disabled={testing} className="wofy-btn-ghost" data-testid="broadcast-test">
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube2 className="w-4 h-4" />} Test send to me
          </button>
          <button type="submit" disabled={busy || !preview?.recipient_count} className="wofy-btn-primary" data-testid="broadcast-send">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send to {preview?.recipient_count ?? "?"}
          </button>
        </div>
      </form>
    </div>
  );
}
